import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, MapPin, ClipboardList, Images, Upload, MessageCircle } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { rupiah } from "@/lib/api";
import GalleryLinks from "@/components/GalleryLinks";

const fade = (i = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.08, duration: 0.5 } });

function Hero({ s }) {
  return (
    <section className="relative overflow-hidden noise">
      <img src="https://images.pexels.com/photos/40256467/pexels-photo-40256467.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-black/40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 md:pt-32 md:pb-36">
        <motion.span {...fade(0)} className="inline-block brush px-4 py-1 font-display font-extrabold uppercase text-lg italic">Paket Foto Atlet</motion.span>
        <motion.h1 {...fade(1)} data-testid="hero-title" className="font-display font-black uppercase italic tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-8xl mt-6 max-w-4xl">
          Abadikan Aksi <span className="text-[#FF5A00]">Sang Juara!</span>
        </motion.h1>
        <motion.p {...fade(2)} className="mt-6 text-base md:text-lg text-neutral-300 max-w-xl">
          Foto profesional {s?.event_name || "Kejurprov Jawa Timur 2026 Sepatu Roda"}. Pilih fotomu di galeri, isi form, upload bukti transfer — beres.
        </motion.p>
        <motion.div {...fade(3)} className="mt-6 flex flex-wrap gap-4 text-sm text-neutral-300">
          <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF5A00]" />{s?.event_venue}</span>
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#FF5A00]" />{s?.event_date}</span>
        </motion.div>
        <motion.div {...fade(4)} className="mt-10 flex flex-wrap gap-3">
          <Link to="/pesan" data-testid="hero-order-btn" className="bg-[#FF5A00] hover:bg-[#E04F00] active:scale-95 transition-[background-color,transform] duration-150 px-8 py-4 font-display font-extrabold uppercase text-xl tracking-wide">
            Pesan Foto Sekarang
          </Link>
          <a href="#galeri" data-testid="hero-gallery-btn" className="border border-white/30 hover:border-white active:scale-95 transition-[border-color,transform] duration-150 px-8 py-4 font-display font-bold uppercase text-xl tracking-wide">
            Lihat Galeri
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Pricing({ prices }) {
  const tiers = [
    { k: "1", label: "1 Mata Lomba" },
    { k: "2", label: "2 Mata Lomba" },
    { k: "3", label: "3 / 4 Mata Lomba", hot: true },
  ];
  return (
    <section id="harga" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <h2 className="font-display font-black uppercase italic text-4xl sm:text-5xl"><span className="text-[#FF5A00]">Harga</span> per anak</h2>
      <p className="text-neutral-400 mt-2 text-sm md:text-base">Semua foto atlet pada mata lomba yang dipilih.</p>
      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {tiers.map((t, i) => (
          <motion.div key={t.k} {...fade(i)} data-testid={`price-card-${t.k}`}
            className={`relative p-8 border ${t.hot ? "border-[#FF5A00] bg-[#FF5A00]/10 shadow-[0_0_30px_rgba(255,90,0,0.2)]" : "border-white/10 bg-[#121212]"}`}>
            {t.hot && <span className="absolute -top-3 right-4 bg-[#FF5A00] px-3 py-0.5 text-xs font-bold uppercase">Paling Hemat</span>}
            <p className="font-display uppercase font-bold text-xl text-neutral-300">{t.label}</p>
            <p className="font-display font-black text-5xl mt-4">{rupiah(prices?.[t.k])}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Steps() {
  const steps = [
    { icon: Images, t: "Pilih Foto", d: "Buka galeri Google Drive (Day 1, Day 2, Day 3, UPP & Luar Lintasan) dan catat nama file foto anak Anda." },
    { icon: ClipboardList, t: "Isi Form", d: "Isi data atlet, mata lomba, dan nama file foto. Harga dihitung otomatis." },
    { icon: Upload, t: "Upload Bukti", d: "Transfer ke rekening yang tertera, lalu unggah bukti pembayaran." },
    { icon: MessageCircle, t: "Terima via WA", d: "Admin memverifikasi dan mengirim foto resolusi penuh lewat WhatsApp." },
  ];
  return (
    <section className="border-y border-white/10 bg-[#0b0b0b] speed-lines">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-display font-black uppercase italic text-4xl sm:text-5xl">Cara Pesan</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-10">
          {steps.map((s, i) => (
            <motion.div key={s.t} {...fade(i)} className="bg-[#0b0b0b] p-6">
              <span className="font-display font-black text-6xl text-[#FF5A00]/30">0{i + 1}</span>
              <s.icon className="w-6 h-6 text-[#FF5A00] mt-2" />
              <p className="font-display font-extrabold uppercase text-2xl mt-3">{s.t}</p>
              <p className="text-neutral-400 text-sm mt-2">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { settings } = useSettings();
  return (
    <div data-testid="landing-page">
      <Hero s={settings} />
      <section id="galeri" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="font-display font-black uppercase italic text-4xl sm:text-5xl">Galeri Foto</h2>
            <p className="text-neutral-400 mt-2 text-sm md:text-base">Cari foto anak Anda, lalu catat nama file-nya untuk diisi di form pesanan.</p>
          </div>
          <Link to="/pesan" data-testid="gallery-order-link" className="flex items-center gap-1 text-[#FF5A00] font-display uppercase font-bold text-lg hover:gap-2 transition-[gap]">Sudah pilih? Pesan <ArrowUpRight className="w-5 h-5" /></Link>
        </div>
        <GalleryLinks links={settings?.drive_links} />
      </section>
      <Pricing prices={settings?.prices} />
      <Steps />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
        <img src="/assets/poster.png" alt="Poster Paket Foto Lensa Juara" className="w-full max-w-md border border-white/10" />
        <div>
          <h2 className="font-display font-black uppercase italic text-4xl sm:text-5xl">Setiap Perjuangan <span className="text-[#FF5A00]">Layak Diabadikan</span></h2>
          <p className="text-neutral-400 mt-4">Tim fotografer LENSA JUARA hadir di setiap lintasan untuk menangkap momen terbaik atlet Anda.</p>
          <Link to="/pesan" data-testid="bottom-order-btn" className="inline-block mt-8 bg-[#FF5A00] hover:bg-[#E04F00] active:scale-95 transition-[background-color,transform] duration-150 px-8 py-4 font-display font-extrabold uppercase text-xl">Pesan Foto</Link>
        </div>
      </section>
    </div>
  );
}
